// @ts-nocheck
/*
 * Port of session-replay/components/TimelineScrubber.js (127 lines).
 * BYTE-IDENTICAL. Horizontal seek bar with:
 *   - buffered background (width = bufferedFraction)
 *   - hatched idle ranges
 *   - live progress overlay
 *   - clickable event markers (line for screen_change, dot otherwise)
 * Clicking the bar seeks by fraction; markers stop propagation and seek
 * exactly to their own timestamp.
 */
import React, { useRef, useCallback } from 'react';
import { markerColor } from '../replayUtils';

export default function TimelineScrubber({
  durationMs,
  currentMs,
  bufferedFraction = 1,
  events = [],
  idleRanges = [],
  sessionStartTs,
  onSeek,
}: any) {
  const barRef = useRef<HTMLDivElement | null>(null);

  const frac = (ts: any) => {
    if (!durationMs || !sessionStartTs) return 0;
    return Math.min(
      1,
      Math.max(0, (new Date(ts).getTime() - new Date(sessionStartTs).getTime()) / durationMs),
    );
  };

  const handleClick = useCallback(
    (e: any) => {
      if (!barRef.current || !durationMs) return;
      const rect = barRef.current.getBoundingClientRect();
      const fraction = Math.min(
        1,
        Math.max(0, (e.clientX - rect.left) / rect.width),
      );
      onSeek?.(fraction * durationMs);
    },
    [durationMs, onSeek],
  );

  const progress = durationMs ? Math.min(1, currentMs / durationMs) : 0;

  return (
    <div className="py-2.5">
      <div
        ref={barRef}
        onClick={handleClick}
        className="relative h-7 cursor-pointer overflow-hidden rounded-md border border-border/60 bg-muted/60"
        style={{
          boxShadow:
            'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9)',
        }}
      >
        {/* buffered */}
        <div
          className="absolute inset-y-0 left-0 bg-muted-foreground/15"
          style={{ width: `${bufferedFraction * 100}%` }}
        />
        {/* idle ranges — hatched pattern using theme colors */}
        {idleRanges.map((r: any, i: number) => {
          const left = frac(r.start) * 100;
          const width = Math.max(0.3, (frac(r.end) - frac(r.start)) * 100);
          return (
            <div
              key={`idle-${i}`}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${left}%`,
                width: `${width}%`,
                background:
                  'repeating-linear-gradient(45deg, hsl(var(--muted-foreground) / 0.25), hsl(var(--muted-foreground) / 0.25) 3px, hsl(var(--muted-foreground) / 0.1) 3px, hsl(var(--muted-foreground) / 0.1) 6px)',
              }}
            />
          );
        })}
        {/* progress */}
        <div
          className="absolute inset-y-0 left-0 bg-primary/30"
          style={{
            width: `${progress * 100}%`,
            borderRight: '2px solid hsl(var(--primary))',
          }}
        />
        {/* event markers */}
        {events.map((e: any, i: number) => {
          if (e.event_type === 'scroll_depth' || e.event_type === 'heartbeat') return null;
          const left = frac(e.ts) * 100;
          const isLine = e.event_type === 'screen_change';
          return (
            <div
              key={`m-${i}`}
              title={`${e.event_type}${e.element_label ? `: ${e.element_label}` : ''}`}
              onClick={(ev) => {
                ev.stopPropagation();
                onSeek?.(frac(e.ts) * durationMs);
              }}
              style={
                isLine
                  ? {
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${left}%`,
                      width: 2,
                      background: markerColor(e.event_type),
                      cursor: 'pointer',
                    }
                  : {
                      position: 'absolute',
                      bottom: 3,
                      left: `calc(${left}% - 3px)`,
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: markerColor(e.event_type),
                      cursor: 'pointer',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    }
              }
            />
          );
        })}
      </div>
    </div>
  );
}
